import { FC, useCallback, useEffect, useState } from "react";
import ".././Jobs.scss";
import { useAppDispatch, useAppSelector } from "../../../../store";
import CandidateJobHeader from "../CandidateJobHeader";
import CandidateVacancyCard from "../CandidateVacancyCard";
import {
  DEFAULT_ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_OPTIONS,
} from "../../../../constants/app";
import PaginationControls from "../../../../utils/PaginationControls";
import { fetchPendingJobs } from "../../../../services/candidateJobsService";
import ActivationAlert from "../Elements/ActivationAlert";
import SortControls from "../Elements/SortControls";
import SortFilterDropdown, { SortOrder } from "../../../UI/SortFilterDropdown";
import { timesheetFilterOptions } from "../../../../models/vacancy";
import { TTimesheetSort } from "../../../../store/timesheet/types";
import ExpiringNotifications from "../Elements/ExpiringNotification";

/**
 * The `PendingListing` component is responsible for displaying a list of pending job listings
 * for a candidate. It includes features such as pagination, sorting, and the ability to adjust
 * the number of items displayed per page.
 *
 * ## Features:
 * - Fetches and displays a paginated list of pending jobs for a candidate.
 * - Allows sorting of job listings by various criteria (e.g., creation date).
 * - Provides controls for pagination and adjusting the number of items per page.
 * - Displays job statistics such as total job listings, pending jobs, and accepted jobs.
 *
 * ## Dependencies:
 * - `useAppDispatch`: A custom hook for dispatching Redux actions.
 * - `useAppSelector`: A custom hook for selecting state from the Redux store.
 * - `fetchPendingJobs`: A utility function for fetching pending jobs from the API.
 *
 * ## State Management:
 * - `itemPerPageList`: Tracks the number of items to display per page.
 * - `currentPage`: Tracks the current page number for pagination.
 * - `totalPages`: Tracks the total number of pages available.
 * - `paginationLinks`: Stores the pagination links for navigation.
 * - `sortBy`: Tracks the field by which the jobs are sorted.
 * - `sortOrder`: Tracks the order (ascending or descending) of the sorting.
 *
 * ## Lifecycle:
 * - Fetches the job list when the component mounts or when pagination, sorting, or items per page change.
 * - Updates pagination details when the job list data changes.
 *
 * ## Handlers:
 * - `handlePageChange`: Updates the current page based on user interaction with pagination controls.
 * - `handlePageSizeChange`: Updates the number of items per page and resets to the first page.
 * - `handleTimeFrameSort`: Toggles the sorting order and updates the sorting field.
 *
 * ## Child Components:
 * - `ActivationAlert`: Displays an alert for activation-related information.
 * - `CandidateJobHeader`: Displays job statistics for the candidate.
 * - `SortControls`: Provides controls for sorting the job listings.
 * - `CandidateVacancyCard`: Displays individual job vacancy details.
 * - `PaginationControls`: Provides controls for navigating between pages and adjusting items per page.
 *
 * @component
 * @returns {JSX.Element} The rendered `PendingListing` component.
 */
const PendingListing: FC = () => {
  const dispatch = useAppDispatch();

  const verificationStatus = useAppSelector(
    (state) => state.onboarding.personaldetails?.verification_status
  );
  const documentsExpired = useAppSelector(
    (state) => state.onboarding.userpersonaldetails?.is_documents_expired
  );
  const candidateJobsList = useAppSelector(
    (state) => state.candidateJobs.candidatePendingJobsList
  );
  const candidateJobsListData = candidateJobsList?.pending_jobs;

  const [itemPerPageList, setItemPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<string>("desc");

  /**
   * Fetches the list of pending jobs for a candidate.
   *
   * This function uses the `fetchPendingJobs` utility to retrieve a paginated and sorted
   * list of pending jobs based on the provided parameters. It is memoized using `useCallback`
   * to prevent unnecessary re-creation on re-renders.
   *
   * Dependencies:
   * - `dispatch`: The dispatch function for managing state updates.
   * - `currentPage`: The current page number for pagination.
   * - `itemPerPageList`: The number of items to display per page.
   * - `sortOrder`: The order in which the jobs should be sorted (e.g., ascending or descending).
   * - `sortBy`: The field by which the jobs should be sorted.
   *
   * @async
   * @function getCandidateJobList
   * @returns {Promise<void>} A promise that resolves when the job list has been fetched.
   */
  const getCandidateJobList = useCallback(async () => {
    await fetchPendingJobs(dispatch, {
      page: currentPage,
      per_page: itemPerPageList,
      sort_order: sortOrder,
      sort_by: sortBy,
    });
  }, [dispatch, currentPage, itemPerPageList, sortBy, sortOrder]);

  useEffect(() => {
    getCandidateJobList();
  }, [currentPage, itemPerPageList, getCandidateJobList]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (candidateJobsListData) {
      setTotalPages(candidateJobsListData?.last_page || 1);
      setPaginationLinks(candidateJobsListData?.links);
    }
  }, [candidateJobsListData]);

  /*
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageSizeChange = (value: string | number) => {
    setItemPerPageList(Number(value));
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  /**
   * Handles the sorting of job listings based on the selected criteria.
   * @param type - The type of sorting to be applied (e.g., "created_at", "updated_at").
   */
  const handleTimeFrameSort = (type: string) => {
    setSortBy(type);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleLocalSortChange = (field: string, order: SortOrder) => {
      timesheetFilterOptions.filter(option => {
        if (option.label === field) {
           handleTimeFrameSort(option.value as TTimesheetSort);
          return true;
        }
        return false;
      });
    };
  

  return (
    <>
      <div className="d-flex flex-column gap-20 gap-mobile-1">
        <ActivationAlert />
        { verificationStatus && verificationStatus === "verified" ? <ExpiringNotifications /> : "" }
        <CandidateJobHeader
          jobListingCount={candidateJobsList?.total_job_listing}
          pendingJobCount={candidateJobsList?.total_pending_jobs}
          acceptedJobCount={candidateJobsList?.total_accepted_jobs}
          isCandidateFilledAllForms={candidateJobsList?.is_user_step_exists}
          documentsExpired={documentsExpired}
        />
        <div className="d-flex flex-column">
          {/** Mobile Sort */}
          <SortFilterDropdown
            sortFields={["Position Type", "Requested Timeframe", "Posted", "Applied"]}
            defaultField="Posted"
            onChange={handleLocalSortChange}
            defaultSortOrder={sortOrder as SortOrder}
          />

          {/** Lissting header */}
          <SortControls
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleTimeFrameSort}
            page_type="pending"
          />
          {/** Listing data */}
          {candidateJobsList &&
          candidateJobsList?.pending_jobs?.data?.length > 0 ? (
            candidateJobsList?.pending_jobs?.data?.map((vacancy, index) => (
              <CandidateVacancyCard
                key={index}
                vacancy={vacancy}
                type="pending"
                isCandidateFilledAllForms={
                  candidateJobsList?.is_user_step_exists
                }
                getCandidateJobList={getCandidateJobList}
                documentsExpired={documentsExpired}
              />
            ))
          ) : (
            <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
              <div className="fs-16 fw-400 text-grey-medium">No Jobs Found</div>
            </div>
          )}
        </div>
        {/* Pagination Component */}
        {candidateJobsList &&
          candidateJobsList?.total_pending_jobs > DEFAULT_ITEMS_PER_PAGE && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              paginationLinks={paginationLinks}
              itemsPerPage={itemPerPageList}
              itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handlePageSizeChange}
            />
          )}
        {/* Pagination Component */}
      </div>
    </>
  );
};

export default PendingListing;
